<template>
  <div
    class="h-dvh bg-page-bg dark:bg-page-bg-dark overflow-hidden flex flex-col"
  >
    <Header v-if="user">
      <template v-slot:toggle-sidebar>
        <button
          type="button"
          data-tour="mobile-menu"
          @click="toggleMobileSidebar"
          :aria-label="t('partials.sidebar.toggle')"
          :aria-expanded="mobileSidebarOpen"
          class="lg:hidden flex items-center justify-center w-9 h-9 rounded-md text-heading dark:text-heading-dark hover:bg-secondary-bg dark:hover:bg-secondary-bg-dark transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-main-button-bg/40"
        >
          <mdi-close v-if="mobileSidebarOpen" class="w-5 h-5" />
          <mdi-menu v-else class="w-5 h-5" />
        </button>
      </template>
      <template v-slot:logo>
        <MenuLogo
          class="h-6 md:h-7 w-auto text-heading dark:text-heading-dark"
        />
      </template>
      <template
        v-slot:right-element
        v-if="user.memberships.length > 1 || canViewAsStandard"
      >
        <div class="flex items-center gap-2">
          <ViewAsStandardToggle
            v-if="canViewAsStandard"
            :active="viewAsStandard"
            @toggle="handleToggleViewAsStandard"
          />
          <MembershipSelector
            v-if="user.memberships.length > 1"
            :memberships="user.memberships"
            :selectedMembership="selectedMembership"
            :onSelect="handleSelectOrganization"
          />
        </div>
      </template>
    </Header>

    <div v-if="user" class="relative flex flex-1 min-h-0 overflow-hidden">
      <!-- Desktop: full sidebar, or an icon-only rail when collapsed -->
      <Sidebar
        class="hidden lg:flex h-full"
        :collapsed="sidebarCollapsed"
        collapsible
        @toggle-collapse="toggleSidebar"
      />

      <!-- Mobile / tablet off-canvas drawer -->
      <div
        class="fixed inset-0 z-40 lg:hidden"
        :class="mobileSidebarOpen ? '' : 'pointer-events-none'"
      >
        <div
          class="absolute inset-0 bg-black/40 transition-opacity duration-300"
          :class="mobileSidebarOpen ? 'opacity-100' : 'opacity-0'"
          @click="closeMobileSidebar"
        ></div>
        <Sidebar
          class="relative h-full shadow-xl transition-transform duration-300 ease-in-out"
          :class="mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'"
          @navigate="closeMobileSidebar"
        />
      </div>

      <div class="w-full flex flex-col min-h-0 overflow-hidden">
        <ShellOrgSuspendedBanner />
        <ShellPaymentIssueBanner v-if="showPaymentBanner" />
        <ShellTrialBanner v-if="showPaymentBanner" />
        <!-- Pages arrive with a soft rise: one calm motion across the app.
             Keyed by the route record, so a param change still reuses the
             page as before. -->
        <RouterView v-slot="{ Component, route: viewRoute }">
          <Transition name="page" mode="out-in">
            <div
              :key="viewRoute.matched[viewRoute.matched.length - 1]?.path"
              class="flex flex-1 flex-col min-h-0 overflow-hidden"
            >
              <component :is="Component" />
            </div>
          </Transition>
        </RouterView>
      </div>
    </div>

    <Toast
      v-model:model-value="showOnboardingToast"
      :message="onboardingToastMessage"
      position="bottom-right"
      :duration="6000"
    />

    <Modal
      v-model="showCardUsedDialog"
      :title="t('onboardingCard.cardUsedTitle')"
      size="medium"
    >
      <p class="text-sm text-content dark:text-content-dark">
        {{ t("onboardingCard.cardUsedMessage") }}
      </p>
      <template #footer>
        <div class="flex justify-end p-4 sm:p-6 pt-4">
          <Button variant="primary" type="button" @click="showCardUsedDialog = false">
            {{ t("onboardingCard.cardUsedOk") }}
          </Button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { RouterView, useRoute, useRouter } from "vue-router";
import Header from "../components/Header.vue";
import Sidebar from "../partials/Sidebar.vue";
import ShellPaymentIssueBanner from "../components/Licensing/ShellPaymentIssueBanner.vue";
import ShellTrialBanner from "../components/Licensing/ShellTrialBanner.vue";
import ShellOrgSuspendedBanner from "../components/Licensing/ShellOrgSuspendedBanner.vue";
import Toast from "../components/Base/Toast.vue";
import Modal from "../components/Base/Modal.vue";
import Button from "../components/Base/Button.vue";
import { ONBOARDING_DONE_PARAM } from "../services/session-handler";
import ExtensionService from "../services/extension-service";
import { useAuth } from "../composables/use-auth";
import { useI18n } from "vue-i18n";
import { useSettings } from "../composables/use-settings";
import { useDashboardTour } from "../composables/use-dashboard-tour";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { Config, Role } from "tf-api-client";
import { normalizeUiLocale } from "../utils/i18n";
import { isFeatureEnabled } from "../utils/feature-flags";
import MenuLogo from "../assets/menu-logo.svg";

const router = useRouter();
const { settings, getSettingValue } = useSettings();

const {
  user,
  selectOrganization,
  selectedMembership,
  viewAsStandard,
  canViewAsStandard,
  effectiveRole,
  setViewAsStandard,
} = useAuth();
const { t, locale } = useI18n({ useScope: "global" });

// Failing payment and trial countdown of the selected organization: its
// admins see them on every page (not while viewing as a standard user).
// Tied to the subscription page's feature flag.
const showPaymentBanner = computed(
  () =>
    isFeatureEnabled("/organization/subscription") &&
    !!effectiveRole.value?.includes(Role.OrgAdmin),
);
const mobileSidebarOpen = ref(false);

// Desktop rail preference, remembered across reloads.
const SIDEBAR_COLLAPSED_STORAGE_KEY = "sidebar-collapsed";

const loadSidebarCollapsed = () => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const sidebarCollapsed = ref(loadSidebarCollapsed());

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
  try {
    localStorage.setItem(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      String(sidebarCollapsed.value),
    );
  } catch {
    // Ignore write errors (e.g. storage disabled or quota exceeded).
  }
};

const toggleMobileSidebar = () => {
  mobileSidebarOpen.value = !mobileSidebarOpen.value;
};

const closeMobileSidebar = () => {
  mobileSidebarOpen.value = false;
};

const desktopMediaQuery =
  typeof window !== "undefined"
    ? window.matchMedia("(min-width: 1024px)")
    : null;

const handleDesktopMediaChange = (event: MediaQueryListEvent) => {
  if (event.matches) closeMobileSidebar();
};

watch(
  () => router.currentRoute.value.path,
  () => closeMobileSidebar(),
);

// Just left the card step (see pages/onboarding-card.vue): say so once and
// drop the parameter so a reload does not repeat it.
const route = useRoute();
const showOnboardingToast = ref(false);
const onboardingToastMessage = ref("");

// A card that already had a trial: the paid subscription started at once, a
// dialog rather than a toast so it can't be missed.
const showCardUsedDialog = ref(false);

onMounted(() => {
  const done = route.query[ONBOARDING_DONE_PARAM];
  if (done === "trial" || done === "paid" || done === "cardUsed") {
    if (done === "cardUsed") {
      showCardUsedDialog.value = true;
    } else {
      onboardingToastMessage.value =
        done === "trial"
          ? t("onboardingCard.trialStarted")
          : t("onboardingCard.paidStarted");
      showOnboardingToast.value = true;
    }
    const query = { ...route.query };
    delete query[ONBOARDING_DONE_PARAM];
    router.replace({ path: route.path, query, hash: route.hash });
  }

  if (ExtensionService.isInstalled()) {
    ExtensionService.hasAllAccess().then((hasAccess) => {
      if (!hasAccess) router.push("/install");
    });
  }

  desktopMediaQuery?.addEventListener("change", handleDesktopMediaChange);
});

// Guided tour on a standard user's first login; re-checked when the user or
// the selected organization (and so the role) changes. It waits for the
// settings, which carry the user's language: before they load the UI is
// still in the fallback locale, and the tour's texts are fixed at start.
const { autoStartTour } = useDashboardTour();

const maybeStartTour = () => {
  if (settings.value) autoStartTour();
};

onMounted(maybeStartTour);

watch(
  () => [user.value?.id, selectedMembership.value?.role, settings.value],
  maybeStartTour,
);

onUnmounted(() => {
  desktopMediaQuery?.removeEventListener("change", handleDesktopMediaChange);
  document.body.classList.remove("overflow-hidden");
});

watch(mobileSidebarOpen, (isOpen) => {
  if (typeof document === "undefined") return;
  document.body.classList.toggle("overflow-hidden", isOpen);
});

watch(settings, (newSettings) => {
  if (!newSettings) return;

  const userLocale = normalizeUiLocale(
    getSettingValue(Config.SettingKind.Locale, {
      default: "en",
    }),
  );

  locale.value = userLocale;

  localStorage.setItem("locale", userLocale);
});

// Keep <html lang> in sync with the active UI language so browsers and
// assistive technology pick the right pronunciation and hyphenation.
watch(
  locale,
  (newLocale) => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = String(newLocale);
    }
  },
  { immediate: true },
);

const handleSelectOrganization = (orgId: string) => {
  selectOrganization(orgId);
};

// Switching to the standard view while on an admin-only page would leave the
// user on a page the guard no longer allows, so send them where it would.
const handleToggleViewAsStandard = (enabled: boolean) => {
  setViewAsStandard(enabled);

  const roles = router.currentRoute.value.meta.roles as Role[] | undefined;
  if (roles && !roles.includes(effectiveRole.value)) {
    router.push("/protection/assistant");
  }
};
</script>

<style scoped>
/* Page change: the old page fades out quickly, the new one rises into place. */
.page-enter-active {
  transition:
    opacity 220ms ease-out,
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}
.page-leave-active {
  transition: opacity 90ms ease-in;
}
.page-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.page-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .page-enter-active,
  .page-leave-active {
    transition: none;
  }
  .page-enter-from {
    transform: none;
  }
}
</style>
